import { FunctionAppContext, getFirmSettingsBlobStorageClient } from "../..";
import { FirmSettings, HitStatus, HitStatuses, unexpectedError, validateArray } from "aderant-conflicts-models";
/**
 * Retrieves reviewer options from a blob storage.
 *
 * @param context - The context of the Function App, including logger and other utilities.
 * @returns A promise that resolves to either a `Forbidden` response or an `OkResponse` containing
 *          the reviewer options with `hitShared` and `statusControl` arrays.
 *
 * The function performs the following steps:
 * 1. Logs the start of the operation.
 * 2. Retrieves the blob storage client for firm settings.
 * 3. Checks if the blob `reviewer-options.json` exists:
 *    - If it does not exist, logs a warning and returns default empty arrays.
 * 4. Retrieves the content of the blob:
 *    - If the content is empty or undefined, logs a warning and returns default empty arrays.
 * 5. Parses the blob content as JSON and validates it:
 *    - If the content is invalid, returns default empty arrays.
 * 6. Returns the parsed and validated reviewer options.
 *
 * In case of any error during the process, logs the errors.
 */
export async function getReviewerOptions(context: FunctionAppContext): Promise<any> {
    context.logger.info("Retrieving Reviewer Options.");
    const blobStore = await getFirmSettingsBlobStorageClient(context);
    const blobName = "reviewer-options.json";

    try {
        let reviewerOptions: FirmSettings.ReviewerOptionsTypes = {
            hitShared: [...FirmSettings.reviewerOptionsSection.defaultValue.hitShared],
            statusControl: [...FirmSettings.reviewerOptionsSection.defaultValue.statusControl]
        };
        const exists = await blobStore.blobExists(blobName);
        if (!exists) {
            context.logger.warn(`Blob ${blobName} does not exist.Returning default values.`);
            return reviewerOptions;
        }

        const { content } = await blobStore.getBlobContent(blobName);
        if (!content || content.length === 0) {
            context.logger.warn("Blob content is empty or undefined.");
            return reviewerOptions;
        }

        reviewerOptions = JSON.parse(content);
        if (!isValidReviewerOptions(reviewerOptions)) {
            throw unexpectedError("Validation failed for reviewer options: Invalid format.", "getReviewerOptions");
        }
        return reviewerOptions;
    } catch (error) {
        context.logger.error(`Error retrieving reviewer options: ${error}`);
        throw unexpectedError(`Error retrieving reviewer options: ${error}`, "getReviewerOptions");
    }
}

const allHitStatuses = Object.values(HitStatuses);

// Define the reusable validation function
const isValidHitStatus = (status: HitStatus) => allHitStatuses.includes(status);

function isValidReviewerOptions(data: FirmSettings.ReviewerOptionsTypes): boolean {
    if (!data || typeof data !== "object") {
        return false;
    }

    if (!validateArray(data.hitShared, isValidHitStatus)) {
        return false;
    }

    if (!validateArray(data.statusControl, isValidHitStatus)) {
        return false;
    }

    return true;
}
